import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Table } from '@douyinfe/semi-ui';
import { IconHistogram, IconGridView } from '@douyinfe/semi-icons';
import { useTheme } from '../context/Theme';
import { formatCompact, formatFull } from '../helpers/usage';
import CollapsiblePanel from './CollapsiblePanel';

// 图表尺寸常量
const CHART_HEIGHT = 260;
const MARGIN = { top: 16, right: 8, bottom: 28, left: 52 };
const MAX_BAR_WIDTH = 24;
const SEGMENT_GAP = 2; // 堆叠段之间的表面色间隙

const METRICS = [
  { key: 'cost', label: '花费' },
  { key: 'tokens', label: 'Tokens' },
  { key: 'requests', label: '请求数' },
];

// 回调式 ref：容器节点每次重新挂载（如表格视图切回图表视图）都重新绑定观察器，
// 否则新节点不会被观察，宽度停留在卸载时上报的 0，图表无法渲染
function useContainerWidth() {
  const nodeRef = useRef(null);
  const roRef = useRef(null);
  const [width, setWidth] = useState(0);
  const setRef = useCallback((node) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    nodeRef.current = node;
    if (node) {
      setWidth(node.getBoundingClientRect().width);
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) setWidth(entry.contentRect.width);
      });
      ro.observe(node);
      roRef.current = ro;
    }
  }, []);
  return [setRef, width, nodeRef];
}

// 生成整洁的 y 轴刻度（1/2/2.5/5 × 10^k）
function niceTicks(max, count = 4) {
  if (!(max > 0)) return [0, 1, 2, 3, 4];
  const rawStep = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let step;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 2.5) step = 2.5;
  else if (norm <= 5) step = 5;
  else step = 10;
  step *= mag;
  const ticks = [];
  for (let v = 0; v <= max + step * 1e-6; v += step) {
    ticks.push(parseFloat(v.toPrecision(10)));
  }
  return ticks;
}

// 顶端 4px 圆角、底部方角的柱形路径
function roundedTopRect(x, y, w, h, radius) {
  const r = Math.min(radius, w / 2, h);
  if (h <= 0 || w <= 0) return '';
  if (r <= 0) return `M${x},${y} h${w} v${h} h${-w} Z`;
  return [
    `M${x},${y + h}`,
    `v${-(h - r)}`,
    `a${r},${r} 0 0 1 ${r},${-r}`,
    `h${w - 2 * r}`,
    `a${r},${r} 0 0 1 ${r},${r}`,
    `v${h - r}`,
    'Z',
  ].join(' ');
}

function formatCostTick(v) {
  if (v === 0) return '0';
  if (v >= 1) return String(parseFloat(v.toPrecision(6)));
  return String(v);
}

function formatCostValue(v) {
  return v.toFixed(2);
}

const UsageTrendPanel = ({ days }) => {
  const theme = useTheme();
  const [metric, setMetric] = useState('cost');
  const [showTable, setShowTable] = useState(false);
  const [hover, setHover] = useState(null); // { index, px }
  const [wrapRef, width, wrapNode] = useContainerWidth();

  const isDark = theme === 'dark';
  const colors = {
    series1: isDark ? '#3987e5' : '#2a78d6',
    series2: '#008300',
    grid: isDark ? '#2c2c2a' : '#e1e0d9',
    axisText: '#898781',
    surface: isDark ? '#1a1a19' : '#fcfcfb',
    wash: isDark ? 'rgba(57,135,229,0.10)' : 'rgba(42,120,214,0.07)',
  };

  // 每个指标的系列定义
  const seriesDef = useMemo(() => {
    if (metric === 'cost') {
      return [{ key: 'usd', label: '花费', color: colors.series1, format: formatCostValue }];
    }
    if (metric === 'tokens') {
      return [
        { key: 'promptTokens', label: '提示', color: colors.series1, format: formatFull },
        { key: 'completionTokens', label: '补全', color: colors.series2, format: formatFull },
      ];
    }
    return [{ key: 'requests', label: '请求数', color: colors.series1, format: formatFull }];
  }, [metric, colors.series1, colors.series2]);

  const stacked = seriesDef.length > 1;

  const maxValue = useMemo(() => {
    let max = 0;
    for (const d of days) {
      const total = seriesDef.reduce((s, sd) => s + (d[sd.key] || 0), 0);
      if (total > max) max = total;
    }
    return max;
  }, [days, seriesDef]);

  const ticks = useMemo(() => niceTicks(maxValue), [maxValue]);
  const yMax = ticks[ticks.length - 1] || 1;

  const innerW = Math.max(width - MARGIN.left - MARGIN.right, 0);
  const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
  const n = days.length;
  const bandW = n > 0 ? innerW / n : 0;
  const barW = Math.max(Math.min(MAX_BAR_WIDTH, bandW * 0.62), 2);
  const yScale = (v) => innerH - (v / yMax) * innerH;

  // x 轴标签抽样：保证标签之间至少 52px
  const labelEvery = Math.max(1, Math.ceil(52 / Math.max(bandW, 1)));

  const tickFormat = metric === 'cost' ? formatCostTick : formatCompact;

  const handleBandHover = (index, event) => {
    if (!wrapNode.current) return;
    const rect = wrapNode.current.getBoundingClientRect();
    setHover({ index, px: event.clientX - rect.left });
  };

  const tooltipFor = (index) => {
    const d = days[index];
    if (!d) return null;
    const total = seriesDef.reduce((s, sd) => s + (d[sd.key] || 0), 0);
    const left = Math.min(
      Math.max((hover?.px ?? 0) + 14, 8),
      Math.max(width - 190, 8)
    );
    return (
      <div className="chart-tooltip" style={{ left, top: 8 }}>
        <div className="tt-title">{d.date} · {d.requests} 次请求</div>
        {seriesDef.map((sd) => (
          <div className="tt-row" key={sd.key}>
            <span className="tt-key">
              <span className="key-line" style={{ background: sd.color }} />
              {sd.label}
            </span>
            <span className="tt-val">{sd.format(d[sd.key] || 0)}</span>
          </div>
        ))}
        {stacked && (
          <div className="tt-row">
            <span className="tt-key">合计</span>
            <span className="tt-val">{formatFull(total)}</span>
          </div>
        )}
      </div>
    );
  };

  const tableColumns = [
    { title: '日期', dataIndex: 'date', width: 110 },
    { title: '请求数', dataIndex: 'requests', render: formatFull },
    { title: '提示', dataIndex: 'promptTokens', render: formatFull },
    { title: '补全', dataIndex: 'completionTokens', render: formatFull },
    { title: '缓存读取', dataIndex: 'cacheRead', render: formatFull },
    { title: '缓存写入', dataIndex: 'cacheWrite', render: formatFull },
    { title: '总 Tokens', dataIndex: 'totalTokens', render: formatFull },
    { title: '花费', dataIndex: 'usd', render: (v) => v.toFixed(2) },
  ];

  const headExtra = (
    <>
      {stacked && !showTable && (
        <div className="chart-legend">
          {seriesDef.map((sd) => (
            <span className="legend-item" key={sd.key}>
              <span className="legend-swatch" style={{ background: sd.color }} />
              {sd.label}
            </span>
          ))}
        </div>
      )}
      {/* 表格视图包含全部指标列，无需指标切换，直接隐藏 */}
      {!showTable && (
        <div className="seg-group" role="tablist" aria-label="指标切换">
          {METRICS.map((m) => (
            <button
              key={m.key}
              className={`seg-btn${metric === m.key ? ' active' : ''}`}
              onClick={() => setMetric(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}
      <button
        className="icon-btn"
        title={showTable ? '切换为图表视图' : '切换为表格视图'}
        aria-label={showTable ? '切换为图表视图' : '切换为表格视图'}
        onClick={() => setShowTable(!showTable)}
      >
        {showTable ? <IconHistogram /> : <IconGridView />}
      </button>
    </>
  );

  return (
    <CollapsiblePanel
      className="fade-up fade-up-2"
      storageKey="collapse:daily-usage"
      title="每日用量"
      sub={days.length > 0 ? `${days[0].date} ~ ${days[days.length - 1].date} · ${days.length} 天` : ''}
      extra={headExtra}
    >
      {days.length === 0 ? (
        <div className="chart-empty">暂无数据，查询令牌后展示每日用量趋势</div>
      ) : showTable ? (
        <Table
          columns={tableColumns}
          dataSource={[...days].reverse()}
          rowKey="date"
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
        />
      ) : (
        <div className="chart-wrap" ref={wrapRef} onMouseLeave={() => setHover(null)}>
          {width > 0 && (
            <svg className="chart-svg" width={width} height={CHART_HEIGHT} role="img" aria-label="每日用量柱状图">
              {/* 网格线与 y 轴刻度 */}
              {ticks.map((t) => {
                const y = MARGIN.top + yScale(t);
                return (
                  <g key={t}>
                    <line
                      x1={MARGIN.left}
                      x2={width - MARGIN.right}
                      y1={y}
                      y2={y}
                      stroke={colors.grid}
                      strokeWidth="1"
                    />
                    <text
                      x={MARGIN.left - 8}
                      y={y + 3.5}
                      textAnchor="end"
                      fontSize="11"
                      fill={colors.axisText}
                    >
                      {tickFormat(t)}
                    </text>
                  </g>
                );
              })}

              {/* 柱与交互层 */}
              {days.map((d, i) => {
                const bx = MARGIN.left + i * bandW;
                const cx = bx + (bandW - barW) / 2;
                const isHovered = hover?.index === i;
                let stackTop = MARGIN.top + innerH; // 从基线向上堆叠
                const segments = [];
                seriesDef.forEach((sd, si) => {
                  const v = d[sd.key] || 0;
                  if (v <= 0) return;
                  let h = (v / yMax) * innerH;
                  const isTopmost = seriesDef
                    .slice(si + 1)
                    .every((next) => (d[next.key] || 0) <= 0);
                  // 段间留 2px 表面色间隙（非最底段从底部收缩）
                  const gap = segments.length > 0 ? SEGMENT_GAP : 0;
                  const y = stackTop - h;
                  stackTop = y;
                  const segH = Math.max(h - gap, 0.5);
                  segments.push(
                    isTopmost ? (
                      <path
                        key={sd.key}
                        d={roundedTopRect(cx, y, barW, segH, 4)}
                        fill={sd.color}
                        opacity={isHovered ? 0.82 : 1}
                      />
                    ) : (
                      <rect
                        key={sd.key}
                        x={cx}
                        y={y}
                        width={barW}
                        height={segH}
                        fill={sd.color}
                        opacity={isHovered ? 0.82 : 1}
                      />
                    )
                  );
                });

                const total = seriesDef.reduce((s, sd) => s + (d[sd.key] || 0), 0);
                const label = `${d.date}：${seriesDef
                  .map((sd) => `${sd.label} ${sd.format(d[sd.key] || 0)}`)
                  .join('，')}`;

                return (
                  <g
                    key={d.date}
                    className="band"
                    tabIndex={0}
                    role="img"
                    aria-label={label}
                    onMouseMove={(e) => handleBandHover(i, e)}
                    onFocus={() => setHover({ index: i, px: bx + bandW / 2 })}
                    onBlur={() => setHover(null)}
                  >
                    {/* 悬停背景洗染 */}
                    <rect
                      className="band-indicator"
                      x={bx}
                      y={MARGIN.top}
                      width={bandW}
                      height={innerH}
                      fill={colors.wash}
                      opacity={isHovered ? 1 : 0}
                      rx="4"
                    />
                    {segments}
                    {total <= 0 && (
                      /* 零值日：基线上一个 2px 的淡标记，保证可感知 */
                      <rect
                        x={cx}
                        y={MARGIN.top + innerH - 2}
                        width={barW}
                        height={2}
                        fill={colors.grid}
                      />
                    )}
                    {/* 命中区域覆盖整个 band */}
                    <rect
                      x={bx}
                      y={MARGIN.top}
                      width={bandW}
                      height={innerH}
                      fill="transparent"
                    />
                  </g>
                );
              })}

              {/* 基线 */}
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={MARGIN.top + innerH}
                y2={MARGIN.top + innerH}
                stroke={colors.grid}
                strokeWidth="1"
              />

              {/* x 轴标签（抽样，避免拥挤） */}
              {days.map((d, i) => {
                if (i % labelEvery !== 0 && i !== n - 1) return null;
                // 末尾标签与前一个抽样标签太近时跳过前者
                if (i !== n - 1 && n - 1 - i < labelEvery && (n - 1) % labelEvery !== 0) return null;
                const x = MARGIN.left + i * bandW + bandW / 2;
                return (
                  <text
                    key={`xl-${d.date}`}
                    x={x}
                    y={CHART_HEIGHT - 8}
                    textAnchor="middle"
                    fontSize="11"
                    fill={colors.axisText}
                  >
                    {d.date.slice(5)}
                  </text>
                );
              })}
            </svg>
          )}
          {hover != null && tooltipFor(hover.index)}
        </div>
      )}
    </CollapsiblePanel>
  );
};

export default UsageTrendPanel;
